/*
  The avatar (stage א8, note 5): <amor-avatar> lays items on a thin-line
  figure (snippets/avatar). Items come from the JSON in [data-avatar-data]
  when the element connects, or from setItems(). Each item: h handle, t
  title, u address, s spot on the figure, w world (women, men, both), v the
  variant for the quick add, i the image, o owned (Your Private Edit).
  - One item per zone of the body, the latest in the list wins (a list
    goes oldest first). A dress takes the top and the bottom, so it
    shows only while both are free.
  - The figure: more men's items than women's, the man (data-figure); every
    item a men's item: data-all-men. CSS shows her figure when she is signed
    in, unless data-all-men.
  - An item is the quick add's button (components.js listens for
    [data-quick-add]); an item she already owns, or without a handle (the
    fixed combinations of Your Private Edit's view, stage א9), is only a picture.
  Stage א10 (note 3): an item can be placed by hand, for the fixed
  combinations: p [x, y, width] is the box of the piece itself, in % of the
  board from its top left, f mirrors it, r turns it (degrees), z sets its
  layer. Placed items all show (two shoes, one on each foot). Their Stone
  background is taken off in a canvas, from the edges in, so a piece covers
  the figure like clothes and lets it show around it; if the canvas cannot
  read the image, the image blends with the board as before.
*/
(() => {
  // The zone of the body each spot takes; a dress takes two.
  const ZONES = {
    hat: ['head'],
    eyes: ['head'],
    top: ['top'],
    bottom: ['bottom'],
    dress: ['top', 'bottom'],
    shoes: ['shoes'],
    bag: ['bag'],
    neck: ['accessories'],
    waist: ['accessories'],
    side: ['accessories'],
  };

  /** The items shown: from the latest back, each one whose zones are all still free. */
  const winners = (items) => {
    const taken = new Set();
    const shown = [];
    [...items].reverse().forEach((item) => {
      const zones = ZONES[item?.s];
      if (!zones || !item.i || zones.some((zone) => taken.has(zone))) return;
      zones.forEach((zone) => taken.add(zone));
      shown.push(item);
    });
    return shown.reverse();
  };

  // Closer to the image's Stone than this is background (largest channel difference); up to SOFT
  // it fades out. The Stone is read from the image's edges (#f2f0eb arrives as about 241,240,236,
  // give or take 2), and it is even, so a light piece (white poplin) still stands apart from it.
  const HARD = 5;
  const SOFT = 28;
  // The width of the soft rim of a piece, in pixels of the image.
  const RIM = 3;

  /** The commonest color along the edges of the image: its Stone. */
  const stoneOf = (data, width, height) => {
    const counts = new Map();
    const count = (p) => {
      const key = (data[p * 4] << 16) | (data[p * 4 + 1] << 8) | data[p * 4 + 2];
      counts.set(key, (counts.get(key) || 0) + 1);
    };
    for (let x = 0; x < width; x += 1) {
      count(x);
      count((height - 1) * width + x);
    }
    for (let y = 0; y < height; y += 1) {
      count(y * width);
      count(y * width + width - 1);
    }
    const common = [...counts].sort((a, b) => b[1] - a[1])[0][0];
    return [(common >> 16) & 255, (common >> 8) & 255, common & 255];
  };

  /**
   * The background: what touches the edges of the image through Stone-like
   * pixels, so a cream piece keeps its own light parts. 1: Stone, cleared;
   * 2: almost Stone, the outer edge of the piece (defringe() settles it).
   */
  const flood = (data, width, height, stone) => {
    const size = width * height;
    const distance = new Uint8Array(size);
    for (let p = 0; p < size; p += 1) {
      const o = p * 4;
      distance[p] = Math.max(Math.abs(data[o] - stone[0]), Math.abs(data[o + 1] - stone[1]), Math.abs(data[o + 2] - stone[2]));
    }
    const background = new Uint8Array(size);
    const stack = new Int32Array(size);
    let top = 0;
    const push = (p) => {
      if (!background[p] && distance[p] < SOFT) {
        background[p] = distance[p] < HARD ? 1 : 2;
        stack[top] = p;
        top += 1;
      }
    };
    for (let x = 0; x < width; x += 1) {
      push(x);
      push((height - 1) * width + x);
    }
    for (let y = 0; y < height; y += 1) {
      push(y * width);
      push(y * width + width - 1);
    }
    while (top) {
      top -= 1;
      const p = stack[top];
      // A pixel that is already part of the edge of the piece stops the flood.
      if (distance[p] >= HARD) continue;
      const x = p % width;
      if (x > 0) push(p - 1);
      if (x < width - 1) push(p + 1);
      if (p >= width) push(p - width);
      if (p < size - width) push(p + width);
    }
    for (let p = 0; p < size; p += 1) {
      if (background[p] === 1) data[p * 4 + 3] = 0;
    }
    return { background, distance };
  };

  /**
   * The soft rim of the piece is part Stone: over another piece (a jacket over
   * trousers) it would show as a light line. Each rim pixel takes the color of
   * the piece just inside it, and only as much cover as its blend with the
   * Stone says. Where the piece is too close to the Stone to tell (a light
   * piece), an almost-Stone edge pixel fades out by its distance instead.
   */
  const defringe = (data, width, height, stone, { background, distance }) => {
    const size = width * height;
    const outside = (q) => background[q] === 1;
    const fade = (p) => {
      if (background[p] === 2) data[p * 4 + 3] = Math.round((255 * (distance[p] - HARD)) / (SOFT - HARD));
    };
    const depth = new Uint8Array(size);
    let ring = [];
    for (let p = 0; p < size; p += 1) {
      if (outside(p)) continue;
      const x = p % width;
      if ((x > 0 && outside(p - 1)) || (x < width - 1 && outside(p + 1)) || (p >= width && outside(p - width)) || (p < size - width && outside(p + width))) {
        depth[p] = 1;
        ring.push(p);
      }
    }
    const rings = [ring];
    for (let d = 2; d <= RIM; d += 1) {
      const next = [];
      ring.forEach((p) => {
        const x = p % width;
        [x > 0 ? p - 1 : -1, x < width - 1 ? p + 1 : -1, p - width, p + width].forEach((q) => {
          if (q >= 0 && q < size && !outside(q) && !depth[q]) {
            depth[q] = d;
            next.push(q);
          }
        });
      });
      rings.push(next);
      ring = next;
    }
    const inner = (q) => q >= 0 && q < size && !outside(q);
    for (let d = RIM; d >= 1; d -= 1) {
      rings[d - 1].forEach((p) => {
        const x = p % width;
        let r = 0;
        let g = 0;
        let b = 0;
        let n = 0;
        [x > 0 ? p - 1 : -1, x < width - 1 ? p + 1 : -1, p - width, p + width].forEach((q) => {
          if (inner(q) && (depth[q] === 0 || depth[q] > d)) {
            r += data[q * 4];
            g += data[q * 4 + 1];
            b += data[q * 4 + 2];
            n += 1;
          }
        });
        if (!n) {
          fade(p);
          return;
        }
        const o = p * 4;
        const fr = r / n - stone[0];
        const fg = g / n - stone[1];
        const fb = b / n - stone[2];
        const reach = fr * fr + fg * fg + fb * fb;
        if (reach < 900) {
          fade(p);
          return;
        }
        const cover = ((data[o] - stone[0]) * fr + (data[o + 1] - stone[1]) * fg + (data[o + 2] - stone[2]) * fb) / reach;
        data[o] = Math.round(r / n);
        data[o + 1] = Math.round(g / n);
        data[o + 2] = Math.round(b / n);
        data[o + 3] = Math.round(255 * Math.min(1, Math.max(0, cover)));
      });
    }
    // What is still lighter than the Stone on the rim is the white of the original photo's background.
    rings.forEach((list) =>
      list.forEach((p) => {
        const o = p * 4;
        if (data[o] >= stone[0] - 2 && data[o + 1] >= stone[1] - 2 && data[o + 2] >= stone[2] - 2) data[o + 3] = 0;
      }),
    );
  };

  /** The box of what shows. */
  const boxOf = (data, width, height) => {
    let left = width;
    let right = -1;
    let upper = height;
    let lower = -1;
    for (let p = 0; p < width * height; p += 1) {
      if (data[p * 4 + 3] > 24) {
        const x = p % width;
        const y = (p - x) / width;
        if (x < left) left = x;
        if (x > right) right = x;
        if (y < upper) upper = y;
        if (y > lower) lower = y;
      }
    }
    if (right < left) throw new Error('empty');
    return { left, upper, width: right - left + 1, height: lower - upper + 1 };
  };

  /** The piece without its Stone background, cropped to the piece: a blob URL. One cut-out per image. */
  const cutouts = new Map();
  const cutout = (src) => {
    if (cutouts.has(src)) return cutouts.get(src);
    const job = new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.onerror = reject;
      image.onload = () => {
        try {
          const width = image.naturalWidth;
          const height = image.naturalHeight;
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(image, 0, 0);
          const frame = context.getImageData(0, 0, width, height);
          const stone = stoneOf(frame.data, width, height);
          defringe(frame.data, width, height, stone, flood(frame.data, width, height, stone));
          const box = boxOf(frame.data, width, height);
          context.putImageData(frame, 0, 0);
          const piece = document.createElement('canvas');
          piece.width = box.width;
          piece.height = box.height;
          piece.getContext('2d').drawImage(canvas, box.left, box.upper, box.width, box.height, 0, 0, box.width, box.height);
          piece.toBlob((blob) => (blob ? resolve(URL.createObjectURL(blob)) : reject(new Error('blob'))), 'image/png');
        } catch (error) {
          reject(error);
        }
      };
      image.src = src;
    });
    cutouts.set(src, job);
    return job;
  };

  class AmorAvatar extends HTMLElement {
    connectedCallback() {
      if (this.drawn) return;
      let items = [];
      try {
        items = JSON.parse(this.querySelector('[data-avatar-data]')?.textContent || '[]');
      } catch {
        items = [];
      }
      this.setItems(Array.isArray(items) ? items : []);
    }

    setItems(items) {
      this.drawn = true;
      const list = items.filter(Boolean);
      // Placed by hand (stage א10): every item shows, as placed.
      const shown = list.length && list.every((item) => item.p) ? list : winners(list);
      const men = shown.filter((item) => item.w === 'men').length;
      const women = shown.filter((item) => item.w === 'women').length;
      this.dataset.figure = men > women ? 'man' : 'woman';
      this.toggleAttribute('data-all-men', men > 0 && women === 0);

      const element = this.querySelector('[data-avatar-items]');
      if (!element) return;
      element.replaceChildren(...shown.map((item) => this.buildItem(item)));
    }

    buildItem(item) {
      const li = document.createElement('li');
      li.className = 'avatar__item';
      li.dataset.spot = item.s;
      const image = document.createElement('img');
      image.alt = item.o || !item.h ? item.t || '' : '';
      image.loading = 'lazy';
      if (item.p) this.place(li, image, item);
      else image.src = item.i;
      if (item.o || !item.h) {
        li.append(image);
        return li;
      }
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'avatar__button';
      button.dataset.quickAdd = item.h;
      if (item.v) button.dataset.quickAddVariant = item.v;
      if (item.u) button.dataset.fallbackUrl = item.u;
      button.setAttribute('aria-label', (this.dataset.labelQuickAdd || '__TITLE__').replace('__TITLE__', item.t || ''));
      button.append(image);
      li.append(button);
      return li;
    }

    /** A hand-placed item: its box, mirror, turn and layer; the cut-out once the avatar is about to show. */
    place(li, image, item) {
      const [x, y, width] = item.p;
      li.classList.add('avatar__item--placed');
      li.style.setProperty('--x', `${x}%`);
      li.style.setProperty('--y', `${y}%`);
      li.style.setProperty('--w', `${width}%`);
      if (item.r) li.style.setProperty('--turn', `${item.r}deg`);
      if (item.f) li.style.setProperty('--flip', '-1');
      if (item.z) li.style.zIndex = item.z;
      this.whenNear(() =>
        cutout(item.i)
          .then((url) => {
            image.src = url;
          })
          .catch(() => {
            // The canvas could not read it: the whole image, blended with the board.
            li.classList.add('avatar__item--blend');
            image.src = item.i;
          }),
      );
    }

    /** Runs once the avatar is shown and near the screen (hidden in the other mode until the switch shows it). */
    whenNear(task) {
      if (!('IntersectionObserver' in window)) {
        task();
        return;
      }
      if (!this.pending) {
        this.pending = [];
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            const tasks = this.pending;
            this.pending = null;
            tasks.forEach((run) => run());
          },
          { rootMargin: '400px' },
        );
        observer.observe(this);
      }
      this.pending.push(task);
    }
  }

  customElements.define('amor-avatar', AmorAvatar);
})();
